import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-modal';
import styles from './AddOrderModal.module.css';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { Order, OrderCategory, OrderItemWithoutIdAndStore, OrderPricePreview, OrderReferralDiscount, PickUpPoint } from '../types';
import AsyncSelect from 'react-select/async';
import { loadCustomerOptions } from '../util/loadCustomerOptions';
import Select from 'react-select';
import { loadStoreOptions } from '../util/loadStoreOptions';
import { toast } from 'react-toastify';
import updateNumberValue from '../util/updateNumberValue';
import { formatCurrency } from '../util/formatCurrency';
import { calculateAutoVat } from '../util/vat';
import { suggestedFeeRationale } from '../util/suggestedFeeRationale';
import { DELIVERY_FEE_PRESETS, isPresetDeliveryFee } from '../util/deliveryFeePresets';
import {
  DOCUMENTATION_FEE_PRESETS,
  isPresetDocumentationFee,
  documentationFeeOptionLabel,
} from '../util/documentationFeePresets';
import { requiresVatOrDocumentationFee } from '../util/vatDocumentationExemptStores';
import ConfirmationModal from './ConfirmationModal';
import ReferralDiscountReasonModal from './ReferralDiscountReasonModal';
import {
  formatDiscount,
  isReferralDiscountCapped,
  referralDiscountAmount,
  referralDiscountLabel,
  referralDiscountTerms,
} from '../util/referralDiscount';

const todayIsoDate = () => new Date().toISOString().split('T')[0];

const describePickUpPoint = (point: PickUpPoint) => (point.city ? `${point.name}, ${point.city}` : point.name);

const PRICE_PREVIEW_FEES: { key: Exclude<keyof OrderPricePreview, 'items'>; label: string; deducted?: boolean }[] = [
  { key: 'documentation_fee', label: 'Documentation Fee' },
  { key: 'vat', label: 'VAT' },
  { key: 'payment_charge', label: 'Tini Express Payment Charge' },
  { key: 'delivery_fee', label: 'Tini Express Delivery Fee' },
  { key: 'referral_discount', label: 'Referral Discount', deducted: true },
  { key: 'collection_fee', label: 'Collection Fee' },
  { key: 'credit_amount', label: 'Credit', deducted: true },
];

// Origins the admin form can actually display. Orders raised elsewhere can carry values with no
// option here (e.g. APP_GUEST from guest checkout); copying one would leave the select looking
// empty while still submitting the hidden value, so a copy drops it and the admin re-picks.
const ADMIN_SELECTABLE_ORIGINS = ['WHATSAPP', 'APP', 'WEBSITE'];

// The only item fields a new order is built from (`Admin::OrderService.create_order_item`).
// A duplicated item must shed its id, cost, arrival dates and store object -- those belong to
// the order it was copied from.
const copyableItem = (item: any): OrderItemWithoutIdAndStore => ({
  description: item.description,
  quantity: item.quantity,
  unit_price: item.unit_price,
  store_id: item.store_id,
  should_charge_vat: item.should_charge_vat ?? false,
});

interface AddOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (savedOrder?: any) => void;
  order?: any;
  disableCustomerSelect?: boolean;
  customer?: { label: string; value: string };
  initialData?: any;
  // Source order to copy into a NEW order. Prefills the form the same way `order` does, but
  // the save is a create, not an update -- see `isEditing`.
  duplicateFromOrder?: any;
  onReferralDiscountReleased?: () => void;
}

const AddOrderModal: React.FC<AddOrderModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  order,
  customer,
  disableCustomerSelect,
  initialData,
  duplicateFromOrder,
  onReferralDiscountReleased,
}) => {
  // `order` used to mean both "prefill from this" and "save as an update". Duplicating prefills
  // from an order but still creates, so the two meanings are split: only `order` is an edit.
  const isEditing = Boolean(order?.id);
  const isDuplicating = Boolean(duplicateFromOrder?.id);
  const [date, setDate] = useState('');
  const [creditAmount, setCreditAmount] = useState('0.0');
  const [customerCreditBalance, setCustomerCreditBalance] = useState<number | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<{ label: string; value: string } | null>(null);
  const [deliveryFee, setDeliveryFee] = useState('0.0');
  const [isCustomDeliveryFee, setIsCustomDeliveryFee] = useState(true);
  const [suggestedDeliveryFee, setSuggestedDeliveryFee] = useState<number | null>(null);
  const [suggestedDeliveryFeeMetadata, setSuggestedDeliveryFeeMetadata] = useState<Order['suggested_delivery_fee_metadata']>(null);
  const [note, setNote] = useState('');
  const [vat, setVat] = useState('0.0');
  const [isCustomVat, setIsCustomVat] = useState(false);
  const [documentationFee, setDocumentationFee] = useState('0.0');
  const [isCustomDocumentationFee, setIsCustomDocumentationFee] = useState(false);
  const [items, setItems] = useState<OrderItemWithoutIdAndStore[]>([]);
  // Tracks whether the loaded order was in custom-VAT mode, so switching it to Auto
  // (which overwrites the stored amount) can prompt for confirmation.
  const wasCustomVatOnLoad = useRef(false);
  // Live auto-VAT preview (15% of the items flagged "Charge VAT?"); the backend recomputes
  // authoritatively on save.
  const computedVat = useMemo(() => calculateAutoVat(items), [items]);
  // The per-item "Charge VAT?" control only drives Auto VAT, so it's hidden in Custom mode.
  const showChargeVatColumn = !isCustomVat;
  // In Auto VAT mode we submit the computed amount, not the `vat` state — validate and send
  // the same value so the two never drift apart.
  const effectiveVat = !isCustomVat ? String(computedVat) : vat;
  const [paymentMethod, setPaymentMethod] = useState('');
  const [origin, setOrigin] = useState('');
  const [isCustomerPurchasing, setIsCustomerPurchasing] = useState<string>('no');
  const [errors, setErrors] = useState<{ documentation_fee?: string; vat?: string; date?: string; customerId?: string; items?: string; deliveryFee?: string; storeId?: string; orderItems?: string; isCustomerPurchasing?: string, creditAmount?: string, origin?: string}>({});
  const [categories, setCategories] = useState<{ label: string; value: string }[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<{ label: string; value: string }[]>([]);
  const [stores, setStores] = useState<{ label: string; value: number }[]>([]);
  const [pickUpPoints, setPickUpPoints] = useState<PickUpPoint[]>([]);
  const [pickUpPointId, setPickUpPointId] = useState<number | null>(null);
  // The saved order's own label ("Name, City"), so an edit still reads correctly when the
  // order sits at a point that has since been deactivated and dropped off the list.
  const [pickUpPointLabel, setPickUpPointLabel] = useState<string | null>(null);
  const [showVatOrDocumentationConfirm, setShowVatOrDocumentationConfirm] = useState(false);
  // True when the order carries an item from a non-exempt store yet has neither VAT nor a
  // documentation fee. Drives both the submit check and clearing the error once it's fixed.
  const vatOrDocumentationMissing = useMemo(
    () =>
      Number(effectiveVat) <= 0 &&
      Number(documentationFee) <= 0 &&
      requiresVatOrDocumentationFee(items, stores),
    [effectiveVat, documentationFee, items, stores],
  );
  const [pricePreview, setPricePreview] = useState<OrderPricePreview | null>(null);
  const [previewedInputs, setPreviewedInputs] = useState<string | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [referralDiscount, setReferralDiscount] = useState<OrderReferralDiscount | null>(null);
  const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
  const [isReleasingDiscount, setIsReleasingDiscount] = useState(false);
  const referralDiscountCapped = referralDiscount ? isReferralDiscountCapped(Number(deliveryFee), referralDiscount) : false;
  const liveReferralDiscountAmount = referralDiscount ? referralDiscountAmount(Number(deliveryFee), referralDiscount) : 0;

  const itemsPayload = () => items.map(item => ({ ...item, purchased_by_customer: isCustomerPurchasing === 'yes' }));

  const pricePreviewRequest = () => ({
    order_id: isEditing ? order.id : undefined,
    pick_up_point_id: !isEditing ? pickUpPointId : undefined,
    tini_delivery_fee: deliveryFee,
    vat: effectiveVat,
    vat_is_custom: isCustomVat,
    documentation_fee: documentationFee,
    credit_amount: creditAmount,
    order_items_attributes: itemsPayload(),
  });

  const currentPreviewInputs = JSON.stringify(pricePreviewRequest());
  const isPreviewStale = pricePreview !== null && previewedInputs !== currentPreviewInputs;

  const previewQuote = async () => {
    const inputs = currentPreviewInputs;
    setIsPreviewing(true);
    try {
      const res = await fetchWithAuth('/orders/price_preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: inputs,
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data) {
        setPricePreview(data);
        setPreviewedInputs(inputs);
      } else {
        toast.error(data?.errors?.[0] || 'Could not preview the quote');
      }
    } catch (err) {
      console.error('Error previewing quote:', err);
      toast.error('Could not preview the quote');
    } finally {
      setIsPreviewing(false);
    }
  };

  const releaseReferralDiscount = async (reason: string) => {
    if (!order?.id) return;

    setIsReleasingDiscount(true);
    try {
      const res = await fetchWithAuth(`/orders/${order.id}/release_referral_discount`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        toast.error(data?.errors?.[0] || 'Could not release the referral discount');
        return;
      }
      setReferralDiscount(null);
      setPricePreview(null);
      setPreviewedInputs(null);
      setIsReleaseModalOpen(false);
      toast.success(data.message);
      onReferralDiscountReleased?.();
    } catch {
      toast.error('Could not release the referral discount');
    } finally {
      setIsReleasingDiscount(false);
    }
  };

  const resetForm = () => {
    setReferralDiscount(null);
    setIsReleaseModalOpen(false);
    setPricePreview(null);
    setPreviewedInputs(null);
    setDate('');
    setSelectedCustomer(null);
    setDeliveryFee('0.0');
    setIsCustomDeliveryFee(true);
    setSuggestedDeliveryFee(null);
    setSuggestedDeliveryFeeMetadata(null);
    setNote('');
    setVat('0.0');
    setIsCustomVat(false);
    wasCustomVatOnLoad.current = false;
    setDocumentationFee('0.0');
    setIsCustomDocumentationFee(false);
    setItems([]);
    setPaymentMethod('');
    setOrigin('');
    setErrors({});
    setSelectedCategories([]);
    setIsCustomerPurchasing('');
    setCreditAmount('0.0');
    setCustomerCreditBalance(null);
    setShowVatOrDocumentationConfirm(false);
    setPickUpPointId(null);
    setPickUpPointLabel(null);
  };

  const fetchCustomerCreditBalance = async (customerId: string) => {
    try {
      const res = await fetchWithAuth(`/customers/${customerId}`);
      const data = await res.json();
      setCustomerCreditBalance(data.credit_balance ?? null);
    } catch (error) {
      console.error('Error fetching customer credit balance:', error);
    }
  };

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const res = await fetchWithAuth('/stores?per_page=1000');
        const data = await res.json();
        const formatted = data.stores.map((store: any) => ({ label: store.name, value: store.id }));
        setStores(formatted);
      } catch (error) {
        console.error('Error fetching stores:', error);
      }
    };
    fetchStores();
  }
  , []);

  useEffect(() => {
    const fetchPickUpPoints = async () => {
      try {
        const res = await fetchWithAuth('/pick_up_points?per_page=100');
        const data = await res.json();
        // The admin endpoint lists newest first; show them the way the customer app does,
        // with the default at the top and the rest by name.
        const active = (data.pick_up_points || [])
          .filter((point: PickUpPoint) => point.active)
          .sort((a: PickUpPoint, b: PickUpPoint) =>
            Number(b.default) - Number(a.default) || a.name.localeCompare(b.name),
          );
        setPickUpPoints(active);
      } catch (error) {
        console.error('Error fetching pick-up points:', error);
      }
    };
    fetchPickUpPoints();
  }, []);

  useEffect(() => {
    // Shared by editing and duplicating. `forDuplicate` keeps the fields that describe what was
    // ordered and drops the ones that belong to the specific order being copied.
    const populateFromOrder = (data: any, forDuplicate: boolean) => {
      setDate(forDuplicate ? todayIsoDate() : (data.date || ''));
      if (data.customer) {
        setSelectedCustomer({ label: data.customer.identifier, value: data.customer_id.toString() });
        fetchCustomerCreditBalance(data.customer_id.toString());
      } else {
        setSelectedCustomer(null);
        setCustomerCreditBalance(null);
      }
      setIsCustomerPurchasing(data.is_customer_purchasing ? 'yes' : 'no');
      setPaymentMethod(data.payment_method || '');
      const sourceOrigin = data.origin || '';
      setOrigin(
        forDuplicate && !ADMIN_SELECTABLE_ORIGINS.includes(sourceOrigin) ? '' : sourceOrigin,
      );
      setDeliveryFee(data.tini_delivery_fee || 0.0);
      setIsCustomDeliveryFee(!isPresetDeliveryFee(data.tini_delivery_fee || 0.0));
      // The AI fee suggestion was made for the source order, so a copy starts without one.
      setSuggestedDeliveryFee(forDuplicate ? null : (data.suggested_delivery_fee ?? null));
      setSuggestedDeliveryFeeMetadata(forDuplicate ? null : (data.suggested_delivery_fee_metadata ?? null));
      // Credit is not copied: the balance has moved on since, and the backend rejects an amount
      // above the customer's current balance.
      setCreditAmount(forDuplicate ? '0.0' : (data.credit_amount || 0.0));
      setNote(data.notes || '');
      setVat(data.vat || 0.0);
      setIsCustomVat(data.vat_is_custom ?? false);
      wasCustomVatOnLoad.current = data.vat_is_custom ?? false;
      setDocumentationFee(data.documentation_fee || 0.0);
      setIsCustomDocumentationFee(!isPresetDocumentationFee(data.documentation_fee || 0.0));
      setItems(forDuplicate ? (data.order_items || []).map(copyableItem) : (data.order_items || []));
      // A copy keeps the source's pick-up point, but it is still editable and falls back to
      // the default below if that point has since been deactivated.
      setPickUpPointId(data.pick_up_point_id ?? null);
      setPickUpPointLabel(data.pickup_point_location ?? null);
      setReferralDiscount(forDuplicate ? null : (data.referral_discount ?? null));
      if (data.categories) {
        setSelectedCategories(data.categories.map((category: OrderCategory) => ({ label: category.category, value: category.category })));
      }
    };

    const loadOrder = async (id: number, forDuplicate: boolean) => {
      try {
        const res = await fetchWithAuth(`/orders/${id}`);
        const data = await res.json();
        populateFromOrder(data, forDuplicate);
      } catch (error) {
        console.error('Error fetching order:', error);
      }
    };
    const fetchCategories = async () => {
      try {
        const res = await fetchWithAuth('/orders/categories');
        const data = await res.json();
        const formatted = data.categories.map((category: string) => ({ label: category, value: category }));
        setCategories(formatted);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    if (isOpen) {
      fetchCategories();
      if (initialData) {
        const fetchCustomer = async () => {
          try {
            const res = await fetchWithAuth(`/customers/${initialData.customer_id}`);
            const data = await res.json();
            setSelectedCustomer({ label: data.identifier, value: data.id.toString() });
          } catch (error) {
            console.error('Error fetching customer:', error);
          }
        };
        fetchCustomer();
        setDate(initialData.date || '');
        setIsCustomerPurchasing(initialData.is_tini_making_purchase === 'yes' ? 'no' : 'yes');
        setOrigin(initialData.origin || '');
      } else if (duplicateFromOrder?.id) {
        loadOrder(duplicateFromOrder.id, true);
      } else if (disableCustomerSelect && customer) {
        setSelectedCustomer(customer);
        fetchCustomerCreditBalance(customer.value);
        if (order?.id) loadOrder(order.id, false);
      } else if (order?.id) {
        loadOrder(order.id, false);
      } else {
        resetForm();
      }
    } else {
      resetForm();
    }
  }, [isOpen, order, disableCustomerSelect, customer, initialData, duplicateFromOrder]);

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unit_price: 0, store_id: 0, should_charge_vat: false }]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: keyof OrderItemWithoutIdAndStore, value: string | number | boolean | null) => {
    const updatedItems = [...items];
    (updatedItems[index] as any)[field] = value;
    setItems(updatedItems);
  };

  const handleVatModeChange = (mode: string) => {
    const goingAuto = mode === 'auto';
    // Switching an existing custom order to Auto overwrites its stored VAT — confirm first.
    if (goingAuto && isEditing && wasCustomVatOnLoad.current) {
      const confirmed = window.confirm(
        'Switch to automatic VAT? This recalculates VAT at 15% of the items marked "Charge VAT?" and replaces the current VAT amount.'
      );
      if (!confirmed) return;
    }
    setIsCustomVat(!goingAuto);
    // Seed the amount from the live computed VAT either way, so Custom starts from a
    // sensible figure the admin can adjust rather than a stale value.
    setVat(String(computedVat));
  };

  // Pre-select the system default once the points load, matching what the customer app does.
  // An edit keeps whatever the order already has, since it can't be changed here anyway.
  useEffect(() => {
    if (isEditing || pickUpPoints.length === 0) return;
    if (pickUpPoints.some((point) => point.id === pickUpPointId)) return;

    const preselected = pickUpPoints.find((point) => point.default) ?? pickUpPoints[0];
    setPickUpPointId(preselected.id);
  }, [isEditing, pickUpPoints, pickUpPointId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const newErrors: typeof errors = {};
    if (!selectedCustomer) newErrors.customerId = 'Customer is required.';
    if (!date) newErrors.date = 'Date is required.';
    if (!origin.trim()) newErrors.origin = 'Origin is required.';
    if (items.length === 0) newErrors.items = 'At least one order item is required.';
    if (isNaN(Number(deliveryFee))) newErrors.deliveryFee = 'Delivery fee is required.';
    if (isNaN(Number(effectiveVat))) newErrors.vat = 'VAT is required.';
    if (isNaN(Number(documentationFee))) newErrors.documentation_fee = 'Documentation Fee is required.';
    if (creditAmount && Number(creditAmount) < 0) {
      newErrors.creditAmount = 'Credit amount should be a positive number.';
    }
    if (customerCreditBalance !== null && Number(creditAmount) > customerCreditBalance) {
      newErrors.creditAmount = `Credit amount exceeds customer credit balance of ${formatCurrency(customerCreditBalance)}.`;
    }
    if (items.some(item => !item.store_id || item.store_id === 0)) {
      newErrors.storeId = 'All items must have a valid store selected.';
    }
    const itemErrors = items.some(item =>
      !item.store_id || item.store_id === 0 ||
      !item.description.trim() ||
      isNaN(Number(item.quantity)) || item.quantity <= 0 ||
      !Number.isInteger(Number(item.quantity)) ||
      isNaN(Number(item.unit_price)) || item.unit_price < 0
    );
    if (itemErrors) {
      newErrors.orderItems = 'Each item must have a store, description, quantity and unit price.';
    }
    if (!isCustomerPurchasing) newErrors.isCustomerPurchasing = 'Please select an option.';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // An order can legitimately carry neither VAT nor a documentation fee, so this asks
    // rather than blocks — but the admin has to say so out loud, and that confirmation
    // travels with the save. Asked last, once the form is otherwise valid, so an order that
    // fails validation never leaves a stray prompt behind.
    if (vatOrDocumentationMissing) {
      setShowVatOrDocumentationConfirm(true);
      return;
    }

    await submitOrder(false);
  };

  const submitOrder = async (vatOrDocumentationAcknowledged: boolean) => {
    const orderData: Record<string, any> = {
      customer_id: Number(selectedCustomer?.value),
      date,
      tini_delivery_fee: deliveryFee,
      notes: note,
      vat: effectiveVat,
      vat_is_custom: isCustomVat,
      documentation_fee: documentationFee,
      vat_or_documentation_acknowledged: vatOrDocumentationAcknowledged,
      credit_amount: creditAmount,
      order_items_attributes: itemsPayload(),
      categories: selectedCategories.map(cat => cat.label),
      payment_method: paymentMethod,
      origin,
      is_customer_purchasing: isCustomerPurchasing === 'yes',
    };

    // Only ever sent on create. Moving an order afterwards goes through "Change to Default"
    // on the order, which handles the notification and the collection-fee refund.
    if (!isEditing && pickUpPointId !== null) {
      orderData.pick_up_point_id = pickUpPointId;
    }

    try {
      const method = isEditing ? 'PATCH' : 'POST';
      const url = isEditing ? `/orders/${order.id}` : '/orders';
      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });

      if (res.ok) {
        // The response carries the saved order; a duplicate needs its new id to navigate to.
        const savedOrder = await res.json().catch(() => null);
        onSuccess(savedOrder);
        resetForm();
        onClose();
        toast.success(isEditing ? 'Order updated successfully' : 'Order added successfully');
      } else {
        // The backend enforces the same rules the form does; surface its message rather than
        // failing silently, so a rejected save is visible to the admin.
        const data = await res.json().catch(() => null);
        toast.error(data?.errors?.[0] || (isEditing ? 'Failed to update order' : 'Failed to create order'));
      }
    } catch (err) {
      console.error('Error submitting order:', err);
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={styles.modalContent}
      style={{
        content: {
          maxHeight: '90vh',
          overflowY: 'auto',
        },
      }}
    >
      <h2 className="text-2xl font-bold mb-4">
        {isEditing
          ? 'Edit Order'
          : isDuplicating && duplicateFromOrder?.order_reference
            ? `New Order (copy of ${duplicateFromOrder.order_reference})`
            : 'New Order'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="add-order-date" className="block text-gray-700">Date</label>
            <input id="add-order-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-3 py-2 border rounded" />
            {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
          </div>
          <div>
            <label className="block text-gray-700">Customer</label>
            <AsyncSelect
              cacheOptions
              loadOptions={loadCustomerOptions}
              defaultOptions
              value={selectedCustomer}
              onChange={(selectedOption) => {
                setSelectedCustomer(selectedOption);
                if (selectedOption) {
                  fetchCustomerCreditBalance(selectedOption.value);
                } else {
                  setCustomerCreditBalance(null);
                }
              }}
              placeholder="Select a customer..."
              isDisabled={disableCustomerSelect || Boolean(referralDiscount)}
            />
            {referralDiscount && (
              <p className="text-xs text-gray-500 mt-1">Release the referral discount before changing the customer.</p>
            )}
            {errors.customerId && <p className="text-red-500 text-sm mt-1">{errors.customerId}</p>}
          </div>
        </div>
        <div>
          <label className="block text-gray-700">Categories</label>
          <Select
            isMulti
            options={categories}
            value={selectedCategories}
            onChange={(selected) => setSelectedCategories(selected as { label: string; value: string }[])}
            placeholder="Select categories..."
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-700 mt-4">Origin</label>
            <select
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="">Select origin</option>
              {/* Admins no longer raise website orders by hand; the option only shows for an
                  order that already carries it (a website-form deep link, or an older order). */}
              {origin === 'WEBSITE' && <option value="WEBSITE">WEBSITE</option>}
              <option value="WHATSAPP">WHATSAPP</option>
              <option value="APP">APP</option>
            </select>
            {errors.origin && <p className="text-red-500 text-sm mt-1">{errors.origin}</p>}
          </div>
          <div>
            <label className="block text-gray-700 mt-4">Payment Method</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="">Select payment method</option>
              <option value="EFT">EFT</option>
              <option value="Cash Deposit">Cash Deposit</option>
              <option value="Card">Card</option>
              <option value="eWallet">eWallet (FNB)</option>
              <option value="Send Money">Send Money (Nedbank)</option>
              <option value="Unayo">Unayo (STD Bank)</option>
              <option value="DeltaPay">DeltaPay</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="add-order-pick-up-point" className="block text-gray-700">Pick-Up Point</label>
            <select
              id="add-order-pick-up-point"
              value={pickUpPointId ?? ''}
              onChange={(e) => setPickUpPointId(Number(e.target.value))}
              disabled={isEditing}
              className={`w-full px-3 py-2 border rounded${isEditing ? ' bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
            >
              {isEditing ? (
                <option value={pickUpPointId ?? ''}>{pickUpPointLabel ?? 'Not set'}</option>
              ) : (
                pickUpPoints.map((point) => (
                  <option key={point.id} value={point.id}>{describePickUpPoint(point)}</option>
                ))
              )}
            </select>
          </div>
        </div>
        <div className="flex gap-6 mt-4 items-start">
          <div>
            <label className="block text-gray-700"><b>Is the customer purchasing the items themselves?</b></label>
            <div className="flex space-x-4 mt-1">
              <label>
                <input
                  type="radio"
                  value="yes"
                  checked={isCustomerPurchasing === 'yes'}
                  onChange={(e) => setIsCustomerPurchasing(e.target.value)}
                  className="mr-1"
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  value="no"
                  checked={isCustomerPurchasing === 'no'}
                  onChange={(e) => setIsCustomerPurchasing(e.target.value)}
                  className="mr-1"
                />
                No
              </label>
            </div>
            {errors.isCustomerPurchasing && <p className="text-red-500 text-sm mt-1">{errors.isCustomerPurchasing}</p>}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label htmlFor="vat-mode-select" className="block text-gray-700">VAT</label>
            <select
              id="vat-mode-select"
              value={isCustomVat ? 'custom' : 'auto'}
              onChange={(e) => handleVatModeChange(e.target.value)}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="auto">Auto (15%)</option>
              <option value="custom">Custom</option>
            </select>
            {isCustomVat ? (
              <input
                type="text"
                inputMode="decimal"
                aria-label="Custom VAT amount"
                placeholder="Enter VAT amount"
                value={vat}
                onChange={(e) => {updateNumberValue(e.target.value, setVat)}}
                className="w-full px-3 py-2 border rounded mt-2"
              />
            ) : (
              <input
                type="text"
                readOnly
                disabled
                aria-label="Calculated VAT amount"
                value={formatCurrency(computedVat)}
                className="w-full px-3 py-2 border rounded mt-2 bg-gray-100 text-gray-400 cursor-not-allowed"
              />
            )}
            {errors.vat && <p className="text-red-500 text-sm mt-1">{errors.vat}</p>}
          </div>

          <div>
            <label htmlFor="documentation-fee-select" className="block text-gray-700">Documentation Fee</label>
            <select
              id="documentation-fee-select"
              value={isCustomDocumentationFee ? 'custom' : String(Number(documentationFee))}
              onChange={(e) => {
                const { value } = e.target;
                if (value === 'custom') {
                  setIsCustomDocumentationFee(true);
                } else {
                  setIsCustomDocumentationFee(false);
                  setDocumentationFee(value);
                }
              }}
              className="w-full px-3 py-2 border rounded"
            >
              {DOCUMENTATION_FEE_PRESETS.map((amount) => (
                <option key={amount} value={amount}>{documentationFeeOptionLabel(amount)}</option>
              ))}
              <option value="custom">Custom</option>
            </select>
            {isCustomDocumentationFee ? (
              <input
                type="text"
                inputMode="decimal"
                aria-label="Custom documentation fee amount"
                placeholder="Enter custom amount"
                value={documentationFee}
                onChange={(e) => {updateNumberValue(e.target.value, setDocumentationFee)}}
                className="w-full px-3 py-2 border rounded mt-2"
              />
            ) : (
              <input
                type="text"
                readOnly
                disabled
                aria-label="Documentation fee amount"
                value={formatCurrency(Number(documentationFee))}
                className="w-full px-3 py-2 border rounded mt-2 bg-gray-100 text-gray-400 cursor-not-allowed"
              />
            )}
            {errors.documentation_fee && <p className="text-red-500 text-sm mt-1">{errors.documentation_fee}</p>}
          </div>

          <div>
            <label htmlFor="delivery-fee-select" className="block text-gray-700">Tini Express Delivery Fee</label>
            <select
              id="delivery-fee-select"
              value={isCustomDeliveryFee ? 'custom' : String(Number(deliveryFee))}
              onChange={(e) => {
                const { value } = e.target;
                if (value === 'custom') {
                  setIsCustomDeliveryFee(true);
                } else {
                  setIsCustomDeliveryFee(false);
                  setDeliveryFee(value);
                }
              }}
              className="w-full px-3 py-2 border rounded"
            >
              {DELIVERY_FEE_PRESETS.map((fee) => (
                <option key={fee} value={fee}>E{fee}</option>
              ))}
              <option value="custom">Custom</option>
            </select>
            {isCustomDeliveryFee && (
              <input
                type="text"
                inputMode="decimal"
                aria-label="Custom delivery fee amount"
                placeholder="Enter custom amount"
                value={deliveryFee}
                onChange={(e) => {updateNumberValue(e.target.value, setDeliveryFee)}}
                className="w-full px-3 py-2 border rounded mt-2"
              />
            )}
            {errors.deliveryFee && <p className="text-red-500 text-sm mt-1">{errors.deliveryFee}</p>}
            {referralDiscount && (
              <div className="mt-2">
                <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                  Referral {'\u2212'}{Number(referralDiscount.percent)}% delivery
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  Customer pays <strong>{formatCurrency(Math.max(0, Number(deliveryFee) - liveReferralDiscountAmount))}</strong> for delivery
                  ({formatCurrency(Number(deliveryFee))} less the {formatCurrency(liveReferralDiscountAmount)} referral discount).
                  {referralDiscount.status === 'reserved' && ' Recalculates if you change the fee.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {(suggestedDeliveryFee != null || suggestedDeliveryFeeMetadata?.status === 'needs_quote') && (
          <div>
            <label className="block text-gray-700">AI Suggested Delivery Fee</label>
            <input
              type="text"
              value={suggestedDeliveryFee != null ? formatCurrency(suggestedDeliveryFee) : 'Manual quote suggested'}
              disabled
              className="w-full px-3 py-2 border rounded bg-gray-100 text-gray-400 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 italic mt-1">
              {suggestedFeeRationale(suggestedDeliveryFeeMetadata)}This is an AI-suggested delivery fee and is not editable. The admin should decide what the delivery fee should be.
            </p>
          </div>
        )}

        <div>
          <label className="block text-gray-700">Notes</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />
        </div>

        {referralDiscount && (
          <div>
            <label htmlFor="referral-discount-field" className="block text-gray-700">Referral Discount</label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                id="referral-discount-field"
                type="text"
                readOnly
                disabled
                value={`${referralDiscountTerms(referralDiscount, referralDiscountCapped)}: ${formatDiscount(liveReferralDiscountAmount)}`}
                className="flex-1 min-w-full sm:min-w-0 px-3 py-2 border rounded bg-gray-100 text-gray-400 cursor-not-allowed"
              />
              {referralDiscount.status === 'reserved' ? (
                <>
                  <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full" style={{ background: '#fef3c7', color: '#92400e' }}>
                    Reserved
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsReleaseModalOpen(true)}
                    className="text-red-500 hover:text-red-700 text-sm px-2"
                  >
                    Remove
                  </button>
                </>
              ) : (
                <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Redeemed</span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {referralDiscount.earned_from && `Earned from: ${referralDiscount.earned_from}. `}
              {referralDiscount.status === 'reserved' && `Removing it releases it now and returns it to ${selectedCustomer?.label ?? 'the customer'}'s available discounts.`}
            </p>
          </div>
        )}

        <div>
          <div>
            {customerCreditBalance !== null && (
              customerCreditBalance > 0 ? (
                <span className="inline-block bg-green-800 text-green-300 font-bold px-3 py-1 rounded-full text-sm mb-1">
                  Credit available
                </span>
              ) : (
                <span className="inline-block bg-gray-300 text-gray-600 font-bold px-3 py-1 rounded-full text-sm mb-1">
                  Credit unavailable
                </span>
              )
            )}
            <p className="text-sm text-gray-500 mb-1">
              Customer credit balance is {customerCreditBalance !== null ? formatCurrency(customerCreditBalance) : formatCurrency(0)}
            </p>
            <input
              type="text"
              inputMode="decimal"
              value={creditAmount}
              onChange={(e) => {updateNumberValue(e.target.value, setCreditAmount)}}
              className={`w-full px-3 py-2 border rounded ${(customerCreditBalance ?? 0) <= 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              disabled={(customerCreditBalance ?? 0) <= 0}
            />
          </div>
          {errors.creditAmount && <p className="text-red-500 text-sm mt-1">{errors.creditAmount}</p>}
          {isCustomerPurchasing === 'yes' && (
            <p className="mt-4 mb-2 text-sm text-gray-600 italic">
              These prices are recorded for our internal cost tracking only — the customer won't be charged for these items since they purchased them directly.
            </p>
          )}
          <h3 className="text-xl font-bold mt-4">Order Items</h3>
            <div className={`grid ${showChargeVatColumn ? 'grid-cols-9' : 'grid-cols-8'} gap-2 items-center mb-2`}>
            <label className="col-span-2 text-gray-700 text-sm text-center">Store</label>
            <label className="col-span-3 text-gray-700 text-sm">Description</label>
            <label className="col-span-1 text-gray-700 text-sm text-center">Quantity</label>
            <label className="col-span-1 text-gray-700 text-sm text-center">Unit Price</label>
            {showChargeVatColumn && <label className="col-span-1 text-gray-700 text-sm text-center">Charge VAT?</label>}
            <span className="col-span-1"></span>
            </div>
            {items.map((item, idx) => (
            <div key={idx} className={`grid ${showChargeVatColumn ? 'grid-cols-9' : 'grid-cols-8'} gap-2 items-center mb-2`}>
              <AsyncSelect
              cacheOptions
              loadOptions={loadStoreOptions}
              defaultOptions
              value={item.store_id ? stores.find(store => store.value === item.store_id) || null : null}
              onChange={(selectedOption) => {
                handleItemChange(idx, 'store_id', selectedOption?.value || 0);
              }}
              placeholder="Select a store..."
              className="col-span-2"
              isClearable
              />
              <input
              type="text"
              placeholder="Item Description"
              value={item.description}
              onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
              className="col-span-3 px-2 py-2 border rounded"
              />
              <input
              type="number"
              placeholder="Qty"
              value={item.quantity}
              onChange={(e) => handleItemChange(idx, 'quantity', Number(e.target.value))}
              className="col-span-1 px-1 py-1 border rounded text-center"
              style={{ appearance: 'textfield' }}
              />
              <input
                type="text"
                inputMode="decimal"
                placeholder="Price"
                value={item.unit_price}
                onChange={(e) => handleItemChange(idx, 'unit_price', e.target.value)}
                className={`col-span-1 px-1 py-1 border rounded text-center`}
              />
              {showChargeVatColumn && (
                <select
                  value={item.should_charge_vat ? 'true' : 'false'}
                  onChange={(e) => handleItemChange(idx, 'should_charge_vat', e.target.value === 'true')}
                  className="col-span-1 px-1 py-1 border rounded text-xs"
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              )}
              <button
              type="button"
              onClick={() => removeItem(idx)}
              className="col-span-1 text-red-500 text-sm h-10 flex items-center justify-center"
              >
              🗑️
              </button>
            </div>
            ))}
          <button type="button" onClick={addItem} className="mt-2 border px-4 py-2 rounded">+ Add Item</button>
          {errors.items && <p className="text-red-500 text-sm mt-1">{errors.items}</p>}
          {errors.storeId && <p className="text-red-500 text-sm mt-1">{errors.storeId}</p>}
          {errors.orderItems && <p className="text-red-500 text-sm mt-1">{errors.orderItems}</p>}
        </div>
        {pricePreview && (
          <section
            aria-label="Quote preview"
            className="border-t border-gray-300 pt-4"
          >
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-lg font-bold">Quote preview</h3>
              {!isPreviewStale && <span className="text-sm text-gray-500">Preview, not saved yet</span>}
            </div>
            {isPreviewStale ? (
              <div role="status" className="flex items-start gap-3 rounded-md bg-amber-50 border border-amber-200 p-4">
                <svg className="h-5 w-5 flex-shrink-0 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                  <path d="M21 3v6h-6" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-amber-800">The order has changed since your last preview</p>
                  <p className="text-sm text-amber-700">Press Preview quote to see the updated total.</p>
                </div>
              </div>
            ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-black text-white">
                  <th className="px-3 py-2 text-left w-14">Item</th>
                  <th className="px-3 py-2 text-left w-14">Qty</th>
                  <th className="px-3 py-2 text-center">Description</th>
                  <th className="px-3 py-2 text-right w-32">Cost</th>
                </tr>
              </thead>
              <tbody>
                {pricePreview.items.map((item, idx) => (
                  <tr key={idx} className="bg-white">
                    <td className="px-3 py-2">{idx + 1}</td>
                    <td className="px-3 py-2">{item.quantity}</td>
                    <td className="px-3 py-2">{item.store_name ? `${item.store_name}: ${item.description}` : item.description}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(item.cost)}</td>
                  </tr>
                ))}
                {PRICE_PREVIEW_FEES.filter(({ key }) => pricePreview[key] > 0).map(({ key, label, deducted }) => (
                  <tr key={key} className="bg-gray-100 border-b-2 border-white">
                    <td />
                    <td />
                    <td className="px-3 py-2">
                      {key === 'referral_discount' && referralDiscount
                        ? referralDiscountLabel(referralDiscount, isReferralDiscountCapped(pricePreview.delivery_fee, referralDiscount))
                        : label}
                    </td>
                    <td className="px-3 py-2 text-right">{deducted ? '-' : ''}{formatCurrency(pricePreview[key])}</td>
                  </tr>
                ))}
                <tr className="bg-black text-white font-bold">
                  <td />
                  <td />
                  <td className="px-3 py-2">Total Amount</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(pricePreview.total)}</td>
                </tr>
              </tbody>
            </table>
            )}
          </section>
        )}
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={previewQuote}
            disabled={isPreviewing}
            className="border border-gray-400 text-gray-700 px-4 py-2 rounded disabled:opacity-50"
          >
            {isPreviewing ? 'Previewing...' : 'Preview quote'}
          </button>
          <button
            type="button"
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            {isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>

    {/* Gated on isOpen as well as its own state: the parent modal is never unmounted by its
        callers, and react-modal closes on Escape, which would otherwise leave this dialog
        floating over the page with no form behind it. */}
    <ReferralDiscountReasonModal
      isOpen={isOpen && isReleaseModalOpen}
      title="Remove referral discount"
      description={`Releases the discount from this order straight away and gives it back to ${selectedCustomer?.label ?? 'the customer'} for a later order. The order total and payment link are updated now, before you save.`}
      confirmLabel="Remove discount"
      savingLabel="Removing..."
      placeholder="e.g. Customer wants to keep it for a bigger order"
      isSaving={isReleasingDiscount}
      onConfirm={releaseReferralDiscount}
      onCancel={() => setIsReleaseModalOpen(false)}
    />
    {isOpen && showVatOrDocumentationConfirm && (
      <ConfirmationModal
        isOpen={showVatOrDocumentationConfirm}
        message="This order has no VAT and no documentation fee, and it contains items from a store other than Tini Express, SHEIN or Temu. Save it anyway?"
        onConfirm={() => {
          setShowVatOrDocumentationConfirm(false);
          submitOrder(true);
        }}
        onCancel={() => setShowVatOrDocumentationConfirm(false)}
      />
    )}
    </>
  );
};

export default AddOrderModal;
